import { Head, Link } from '@inertiajs/react'
import { useState } from 'react'
import { withWorkspaceLayout } from '@/app/layouts/WorkspaceLayout'
import { type PageNode } from '@/widgets/sidebar/PageTree'

interface Version {
  id: number
  version_number: number
  author: { name: string }
  created_at: string
  content_md: string
}

interface HistoryProps {
  workspace: { slug: string; name: string }
  page: { title: string; slug: string; path: string }
  sidebar_pages: PageNode[]
  versions: Version[]
}

function History({ workspace, page, versions }: HistoryProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null)

  function toggleExpand(id: number) {
    setExpandedId(expandedId === id ? null : id)
  }

  return (
    <>
      <Head title={`History - ${page.title}`} />
      <div className="mx-auto max-w-4xl px-6 py-8">
        <div className="mb-6">
          <Link
            href={`/${workspace.slug}/${page.path}`}
            className="text-sm text-indigo-600 hover:text-indigo-800"
          >
            &larr; Back to {page.title}
          </Link>
        </div>

        <h1 className="mb-6 text-2xl font-bold text-gray-900">
          Version History for {page.title}
        </h1>

        {versions.length === 0 ? (
          <p className="text-gray-500">No versions yet.</p>
        ) : (
          <div className="space-y-3">
            {versions.map((version) => (
              <div
                key={version.id}
                className="rounded-lg border border-gray-200 bg-white"
              >
                <button
                  onClick={() => toggleExpand(version.id)}
                  className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
                >
                  <div className="flex items-center gap-4">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-sm font-medium text-indigo-700">
                      v{version.version_number}
                    </span>
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        {version.author.name}
                      </span>
                      <span className="ml-2 text-sm text-gray-500">
                        {new Date(version.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <svg
                    className={`h-5 w-5 text-gray-400 transition-transform ${
                      expandedId === version.id ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {expandedId === version.id && (
                  <div className="border-t border-gray-200 px-4 py-3">
                    <pre className="whitespace-pre-wrap rounded bg-gray-50 p-4 text-sm text-gray-800">
                      {version.content_md}
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

History.layout = withWorkspaceLayout
export default History
